import {
  Alert,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { STATUS_CHIP } from "components/ModulePayments/modulePaymentsConstants";
import PaymentReceiptChips from "components/Payments/PaymentReceiptChips";
import { formatMoney } from "utils/utils";
import ModuleInvoiceSummary from "./ModuleInvoiceSummary";
import { useModuleInvoice } from "./moduleInvoiceApi";
import { moduleEntryPath, moduleLabel } from "./moduleLinks";

const Lines = ({ lines = [] }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Description</TableCell>
        <TableCell align="center">Qty</TableCell>
        <TableCell align="right">Unit price</TableCell>
        <TableCell align="center">GST</TableCell>
        <TableCell align="right">Total</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {lines.map((line) => (
        <TableRow key={line.id}>
          <TableCell>{line.description}</TableCell>
          <TableCell align="center">{line.quantity}</TableCell>
          <TableCell align="right">{formatMoney(line.price)}</TableCell>
          <TableCell align="center">{line.gst === "yes" ? "10%" : "-"}</TableCell>
          <TableCell align="right">
            {formatMoney(Number(line.price) * Number(line.quantity))}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const Payments = ({ payments = [] }) =>
  payments.length === 0 ? (
    <Typography variant="body2" color="text.secondary">
      No payments yet.
    </Typography>
  ) : (
    <Stack spacing={1.5} divider={<Divider flexItem />}>
      {payments.map((payment) => {
        const refund = payment.entryType === "REFUND";
        return (
          <div key={payment.id}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" fontWeight={700}>
                {refund ? "-" : ""}
                {formatMoney(payment.amount)}
              </Typography>
              <Chip
                size="small"
                label={STATUS_CHIP[payment.status]?.label ?? payment.status}
                color={STATUS_CHIP[payment.status]?.color ?? "default"}
              />
              {refund && <Chip size="small" variant="outlined" color="warning" label="Refund" />}
              <Typography variant="caption" color="text.secondary">
                {[payment.paymentDate, payment.paymentMethod].filter(Boolean).join(" · ")}
              </Typography>
            </Stack>
            {payment.note && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, whiteSpace: "pre-line" }}
              >
                {payment.note}
              </Typography>
            )}
            {payment.status === "REJECTED" && payment.rejectionFeedback && (
              <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                Rejected: {payment.rejectionFeedback}
              </Typography>
            )}
            <PaymentReceiptChips urls={payment.receiptDocuments || []} />
          </div>
        );
      })}
    </Stack>
  );

const LinkedInvoiceView = ({ invoice }) => {
  const navigate = useNavigate();
  const { data: view } = useModuleInvoice(invoice.moduleType, invoice.moduleId);
  const current = view?.invoice ?? invoice;
  const label = moduleLabel(invoice.moduleType);
  const path = moduleEntryPath(invoice.moduleType, invoice.moduleId);

  return (
    <div className="px-6 md:px-10 py-8 space-y-6">
      <Alert
        severity="info"
        action={
          path && (
            <Button color="inherit" size="small" onClick={() => navigate(path)}>
              Open {label} entry
            </Button>
          )
        }
      >
        Linked to {label}
        {view?.summary ? ` · ${view.summary}` : ""}. Payments are managed in its Payments tab.
      </Alert>
      <ModuleInvoiceSummary invoice={current} />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Lines
        </Typography>
        <Lines lines={current.invoices} />
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Payments
        </Typography>
        <Payments payments={current.payments} />
      </Paper>
    </div>
  );
};

export default LinkedInvoiceView;
